import { NextPageContext } from 'next'
import { ApiResponse, CompanySummary, DataWithCount, User } from '../../types'
import {
  CompanyChartData,
  CompanyData,
  CompanyDownloadFormat,
  GetCompanyContactPersonResult,
  GetCompanyMemoResult,
  GetCompanyStatusResult,
  NewsFeedData,
  OverseaBenchmarkResponseType,
  OverseasCompanyData,
  SaveCompanyMemoResponse,
  StatusesResponse,
  UsersResult,
} from '../../types/api'
import { printError } from '../log'
import {
  basePath,
  callAPI,
  callAPIRaw,
  callAPIWithNoContent,
  callAuthAPI,
} from './common'
import { MyLabelId } from '@/components/pages/companies/reducers/sourcingAnnotationCandidates'
import { SUM_OF_MYLABEL_SETTING, trackEvent } from '../tracking'

export const getCompany = (shortname: string) => {
  return callAPI<ApiResponse<CompanySummary>>(
    `${basePath}/companies/${shortname}`
  )
}

export const getLatestCompanies = async () => {
  const res = await callAPI<ApiResponse<CompanyData[]>>(
    `${basePath}/companies/latest`
  )
  return res.data
}

export const getDailyRanking = async () => {
  let data: CompanyData[] = []
  try {
    const res = await callAPI<ApiResponse<CompanyData[]>>(
      `${basePath}/companies/ranking`
    )
    data = res.data
  } catch (e) {
    printError(e)
  }
  return data
}

export const fetchCompany = async (ctx: NextPageContext, shortname: string) => {
  let company
  try {
    company = await callAuthAPI<
      ApiResponse<Required<CompanyData | OverseasCompanyData>>
    >(ctx, `${basePath}/pages/companies/${shortname}`)
  } catch (e) {
    printError(e)
  }
  return company ? company.data : null
}

export const fetchCompanyByMyname = async (
  ctx: NextPageContext,
  myname: string
) => {
  let company
  try {
    company = await callAuthAPI<ApiResponse<Required<CompanyData>>>(
      ctx,
      `${basePath}/pages/c/${myname}`
    )
  } catch (e) {
    printError(e)
  }
  return company ? company.data : null
}

export const fetchCompanyNewsFeed = async (
  shortname: string,
  options: { offset?: number } = {}
) => {
  const q = `offset=${options?.offset}`
  const res = await callAPI<DataWithCount<NewsFeedData[]>>(
    `${basePath}/companies/${shortname}/news_feed?${q}`
  )
  return res
}

export const fetchCompanyChart = async (shortname: string) => {
  const res = await callAPI<ApiResponse<CompanyChartData>>(
    `${basePath}/companies/${shortname}/chart`,
    { credentials: 'include' }
  )
  return res ? res.data : null
}

export const followCompany = async (shortname: string) => {
  return callAPI<{ following: boolean }>(
    `${basePath}/companies/${shortname}/follow`,
    {
      method: 'POST',
      credentials: 'include',
    }
  )
}

export const unfollowCompany = async (shortname: string) => {
  return callAPI<{ following: boolean }>(
    `${basePath}/companies/${shortname}/unfollow`,
    {
      method: 'POST',
      credentials: 'include',
    }
  )
}

export const getCompanyFollowing = async (shortname: string) => {
  return callAPI<{ following: boolean }>(
    `${basePath}/companies/${shortname}/following`
  )
}

export const requestDownloadCompany = async (
  shortname: string,
  format: CompanyDownloadFormat
) => {
  return callAPI<{ format: CompanyDownloadFormat }>(
    `${basePath}/companies/${shortname}/export.${format}`,
    {
      method: 'POST',
      credentials: 'include',
    }
  )
}

export const fetchOverseaBenchmarkCompanies = async (shortname: string) => {
  try {
    return await callAPI<OverseaBenchmarkResponseType>(
      `${basePath}/companies/${shortname}/oversea_benchmarks`,
      {
        credentials: 'include',
      }
    )
  } catch (e) {
    printError(e)
    return { overseaBenchmarkCompanies: [] }
  }
}

export const getCompanyStatus = async (
  ctx: NextPageContext,
  shortname: string
) => {
  try {
    return await callAuthAPI<GetCompanyStatusResult>(
      ctx,
      `${basePath}/pages/companies/${shortname}/company_status`
    )
  } catch (e) {
    printError(e)
    return { companyStatus: null }
  }
}

export const getCompanyStatuses = async () => {
  try {
    const res = await callAPI<StatusesResponse>(`${basePath}/statuses`)
    return res.statuses
  } catch (e) {
    printError(e)
    return []
  }
}

export const getCompanyContactPerson = async (
  ctx: NextPageContext,
  shortname: string
) => {
  try {
    return await callAuthAPI<GetCompanyContactPersonResult>(
      ctx,
      `${basePath}/pages/companies/${shortname}/company_contact_person`
    )
  } catch (e) {
    printError(e)
    return { contactPerson: null }
  }
}

export const getContactPersons = async () => {
  try {
    return await callAPI<UsersResult>(`${basePath}/users`)
  } catch (e) {
    printError(e)
    return { users: [] }
  }
}

export const getCompanyMemo = async (
  ctx: NextPageContext,
  shortname: string
) => {
  try {
    return await callAuthAPI<GetCompanyMemoResult>(
      ctx,
      `${basePath}/pages/companies/${shortname}/company_memo`
    )
  } catch (e) {
    printError(e)
    return { memo: null }
  }
}

export const saveCompanyMemo = async (
  shortname: string,
  content: string,
  callback: () => void
) => {
  try {
    return await callAPI<SaveCompanyMemoResponse>(
      `${basePath}/pages/companies/${shortname}/company_memo`,
      {
        method: 'PUT',
        credentials: 'include',
        body: JSON.stringify({
          content: content,
        }),
      }
    )
  } catch (e) {
    printError(e)
    callback()
    return null
  }
}

export const registerCompanyStatus = async (
  shortname: string,
  statusId: string
) => {
  try {
    await callAPIWithNoContent(`${basePath}/companies/${shortname}/status`, {
      method: 'PUT',
      credentials: 'include',
      body: JSON.stringify({
        id: statusId,
      }),
    })
  } catch (e) {
    printError(e)
  }
}

export const deleteCompanyStatus = async (shortname: string) => {
  try {
    await callAPIWithNoContent(`${basePath}/companies/${shortname}/status`, {
      method: 'DELETE',
      credentials: 'include',
    })
  } catch (e) {
    printError(e)
  }
}

export const registerCompanyContactPerson = async (
  shortname: string,
  userId: number,
  callback: () => void
) => {
  try {
    await callAPIWithNoContent(
      `${basePath}/companies/${shortname}/contact_person`,
      {
        method: 'PUT',
        credentials: 'include',
        body: JSON.stringify({
          id: userId,
        }),
      }
    )
  } catch (e) {
    printError(e)
    callback()
  }
}

export const deleteCompanyContactPerson = async (
  shortname: string,
  callback: () => void
) => {
  try {
    await callAPIWithNoContent(
      `${basePath}/companies/${shortname}/company_contact_person`,
      {
        method: 'DELETE',
        credentials: 'include',
      }
    )
  } catch (e) {
    printError(e)
    callback()
  }
}

export const registerFirstInterviewDate = async (
  shortname: string,
  firstInterviewDate: string
) => {
  try {
    await callAPIWithNoContent(
      `${basePath}/companies/${shortname}/first_interview_date`,
      {
        method: 'PUT',
        body: JSON.stringify({
          firstInterviewDate: firstInterviewDate,
        }),
      }
    )
  } catch (e) {
    printError(e)
  }
}

export const deleteFirstInterviewDate = async (shortname: string) => {
  try {
    await callAPIWithNoContent(
      `${basePath}/companies/${shortname}/first_interview_date`,
      {
        method: 'DELETE',
      }
    )
  } catch (e) {
    printError(e)
  }
}

export const addMyLabel = (
  shortname: string,
  myLabelId: MyLabelId,
  user: User
) => {
  return callAPIRaw(`${basePath}/v1/companies/${shortname}/my_label`, {
    method: 'PUT',
    body: JSON.stringify({ myLabelId }),
  })
    .then((r) => (trackEvent(user, SUM_OF_MYLABEL_SETTING), r))
    .catch(printError)
}

export const removeMyLabel = (
  shortname: string,
  myLabelId: MyLabelId,
  user: User
) => {
  return callAPIRaw(`${basePath}/v1/companies/${shortname}/my_label`, {
    method: 'DELETE',
    body: JSON.stringify({ myLabelId }),
  })
    .then((r) => (trackEvent(user, SUM_OF_MYLABEL_SETTING), r))
    .catch(printError)
}
